<template>
  <div>
    <div class="row">
      <ul class="col suppliers-actions nav nav-tabs">
        <li v-for="(v, key) in subViews" :key="key" class="nav-item">
          <div
            :class="{ active: view === v }"
            class="nav-link"
            @click="toggleView(v)"
          >
            {{ $t("supplier." + v) }}
          </div>
        </li>
      </ul>
    </div>
    <div class="pt-4">
      <suppliers-adder
        v-on:add="addSupplier"
        v-show="view === 'adder'"
      ></suppliers-adder>

      <suppliers-sender
        :selectedSuppliers="selectedSuppliers"
        :letters="letters"
        :deliveryMethods="deliveryMethods"
        v-on:updateSupplier="updateSupplier"
        v-on:updateList="updateList"
        v-show="view === 'sender'"
      ></suppliers-sender>

      <suppliers-importer
        v-on:import="importSuppliers"
        v-show="view === 'importer'"
        :importing="importingTimer"
        v-on:updateSupplier="updateSupplier"
        v-on:add="addSupplier"
        v-on:updateList="updateList"
      ></suppliers-importer>

      <suppliers-remover
        v-on:remove="remove"
        v-if="view === 'remover'"
        :selectedSuppliers="selectedSuppliers"
      ></suppliers-remover>

      <suppliers-main
        v-if="view === 'main'"
        v-on:select="filterByStatus"
        :supplierStatuses="statuses"
        :suppliers="suppliers"
      ></suppliers-main>
    </div>

    <!-- TABLE -->
    <v-client-table
      class="supplier-table-wrapper"
      ref="supplierTable"
      :key="'tableKey-' + tableKey"
      :options="options"
      :columns="columns"
      :data="suppliers"
    >
      <template slot="afterLimit">
        <div class="pull-left">
          <label>{{ $t("supplier.texts.limit") }}</label>
          <select class="form-control" :key="pagesize" v-model="pagesize">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>

        <div class="dropdown pull-right VueTables__columns-dropdown ml-3">
          <button
            type="button"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="true"
            class="btn btn-secondary dropdown-toggle"
          >
            {{ $t("supplier.texts.columns") }}<span><i class="caret"></i></span>
          </button>
          <ul class="dropdown-menu pl-1">
            <li
              class="dropdown-menu-item"
              @click.stop=""
              v-for="(c, key) in Object.keys(showColumn)"
              :key="key"
            >
              <label>
                <input type="checkbox" v-model="showColumn[c]" />{{
                  $t("supplier." + [c])
                }}</label
              >
            </li>
          </ul>
        </div>
        <ul v-if="view !== 'main'" class="status-list">
          <li>
            <button
              @click="selectByStatus('new')"
              class="btn btn-text text-secondary_2"
            >
              {{ $t("supplier.new") }} {{ statuses.new }}
            </button>
          </li>
          <li>
            <button
              @click="selectByStatus('pending')"
              class="btn btn-text text-danger"
            >
              {{ $t("supplier.pending") }} {{ statuses.pending }}
            </button>
          </li>
          <li>
            <button
              @click="selectByStatus('error')"
              class="btn btn-text text-error"
            >
              {{ $t("supplier.error") }} {{ statuses.error }}
            </button>
          </li>
          <li>
            <button
              @click="selectByStatus('active')"
              class="btn btn-text text-success"
            >
              {{ $t("supplier.active") }} {{ statuses.active }}
            </button>
          </li>
          <li>
            <button
              @click="selectByStatus('all')"
              class="btn btn-text text-primary_2"
            >
              {{ $t("supplier.all") }} {{ statuses.all }}
            </button>
          </li>
        </ul>
      </template>

      <!-- SELECT COLUMN -->
      <template slot="select" slot-scope="props">
        <input
          type="checkbox"
          class="form-control form-control-sm"
          :value="props.row"
          v-model="selectedSuppliers"
        />
      </template>

      <!-- STATUS COLUMN -->
      <template slot="status" slot-scope="props">
        <div
          class="circle"
          :title="statusTexts[props.row.status]"
          :class="{
            circle_green: props.row.status === 'active',
            circle_yellow: props.row.status === 'pending',
            circle_red: props.row.status === 'error',
            circle_gray: props.row.status === 'new',
          }"
        ></div>
      </template>

      <!-- EDITABLE COLUMNS -->
      <template
        v-for="(c, key) in editableTextFields"
        :slot="c"
        slot-scope="props"
      >
        <edit-cell-email
          v-if="c === 'email'"
          :value="props.row.email"
          :key="props.row.supplierId + '' + key"
          column="email"
          v-on:saveEdit="saveEdit(props.row.supplierId, 'email', $event)"
        ></edit-cell-email>

        <edit-cell
          v-else
          :key="props.row.supplierId + '' + key"
          :value="props.row[c]"
          :column="c"
          v-on:saveEdit="saveEdit(props.row.supplierId, c, $event)"
        ></edit-cell>
      </template>

      <!-- LANGUAGE COLUMN -->
      <template slot="language" slot-scope="props">
        <edit-cell-select
          :options="[
            { value: 'fi_FI', name: 'FI' },
            { value: 'en_US', name: 'EN' },
            { value: 'sv_SE', name: 'SV' },
            { value: 'de_DE', name: 'DE' },
          ]"
          :value="props.row.language"
          v-on:saveEdit="saveEdit(props.row.supplierId, 'language', $event)"
        ></edit-cell-select>
      </template>

      <!-- COUNTRY CODE COLUMN -->
      <template slot="countryCode" slot-scope="props">
        <edit-cell-select
          :options="countrycodes"
          :value="props.row.countryCode"
          v-on:saveEdit="saveEdit(props.row.supplierId, 'countryCode', $event)"
        ></edit-cell-select>
      </template>

      <!-- LETTER INFO -->
      <template slot="lastLetterSent" slot-scope="props">
        <span>{{ props.row | formatLastLetterSent }}</span>
      </template>

      <!-- EMAIL INFO -->
      <template slot="lastEmailSent" slot-scope="props">
        <span>{{ props.row | formatLastEmailSent }}</span>
      </template>
    </v-client-table>
    <modal :loading="loading"></modal>
    <div ref="wrapper" class="wrapper1">
      <div :style="widthObj" class="div1"></div>
    </div>
  </div>
</template>

<script>
import EditCell from "../components/suppliers/EditCell";
import EditCellSelect from "../components/suppliers/EditCellSelect";
import EditCellEmail from "../components/suppliers/EditCellEmail";
import TextFilter from "../components/suppliers/TextFilter";
import SelectFilter from "../components/suppliers/SelectFilter";
import SuppliersAdder from "../components/suppliers/SuppliersAdder";
import SuppliersSender from "../components/suppliers/SuppliersSender";
import SuppliersMain from "../components/suppliers/SuppliersMain";
import Modal from "../components/common/Modal";
import SuppliersImporter from "../components/suppliers/SuppliersImporter";
import SuppliersRemover from "../components/suppliers/SuppliersRemover";
import { Event as event } from "vue-tables-2";
import { countrycodes } from "@/assets/countrycodes.js";
import moment from "moment";
export default {
  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      try {
        let res = await vm.$store.dispatch("listLetters");
        vm.letters = res.map((l) => {
          l.blobFI = l.blobFI === "true" ? true : null;
          return l;
        });
        vm.selectedLetter = null;
      } catch (error) {
        console.log(error);
      }
    });
  },
  created: async function () {
    try {
      this.loadShowColumn();
      let data = await this.$store.dispatch("listSuppliers");
      this.suppliers = data;
      this.windowWidth = window.innerWidth;
      this.checkImportStatus();
    } catch (error) {
      console.log(error);
    }
  },
  mounted: async function () {
    try {
      const pagesize = localStorage.getItem("pagesize");
      if (pagesize) {
        this.$refs.supplierTable.setLimit(pagesize);
        this.perPage = pagesize;
      }
      event.$on("vue-tables.limit", (e) => {
        localStorage.setItem("pagesize", e);
      });

      /* GHOST SCROLL */
      const windowListener = () => (this.windowWidth = window.innerWidth);
      this.windowListener = windowListener;

      let table = this.$refs.supplierTable.$el.children[1];
      let wrapper1 = this.$refs.wrapper;
      wrapper1.onscroll = function () {
        table.scrollLeft = wrapper1.scrollLeft;
      };
      table.onscroll = function () {
        wrapper1.scrollLeft = table.scrollLeft;
      };

      const scrollListener = () => {
        if (this.displayScroll) {
          window.$(wrapper1).show();
          let topT = this.top(table);
          let topW = this.top(wrapper1);
          let botT = this.bottom(table);
          let botW = this.bottom(wrapper1);
          if (topT < topW && botT > botW) {
            window.$(wrapper1).show();
          } else {
            window.$(wrapper1).hide();
          }
        }
      };
      this.scrollListener = scrollListener;
      this.$nextTick(() => {
        window.addEventListener("resize", windowListener);
        window.addEventListener("scroll", scrollListener);
      });
      setTimeout(() => {
        this.displayScroll =
          this.$refs.supplierTable.$el.children[1].scrollWidth >
          this.$refs.supplierTable.$el.children[1].clientWidth;
        this.widthObj = {
          width:
            this.$refs.supplierTable.$el.children[1].scrollWidth + 30 + "px",
        };
      }, 1000);
      /* GHOST SCROLL END */
      this.isMounted = true;
    } catch (error) {
      console.log(error);
    }
  },
  beforeDestroy: function () {
    // Destroy ghost scroll
    window.removeEventListener("resize", this.windowListener);
    window.removeEventListener("scroll", this.scrollListener);
    let table = this.$refs.supplierTable.$el.children[1];
    let wrapper1 = this.$refs.wrapper;
    table.onscroll = null;
    wrapper1.onscroll = null;
  },
  filters: {
    formatLastLetterSent: function (row) {
      let date = row.lastLetterSent || "";
      if (date) {
        date = date.substr(0, 10);
        date = moment(date).format("DD.MM.YYYY") + ": ";
        return date + row.lettersSent;
      }
      return "";
    },
    formatLastEmailSent: function (row) {
      let date = row.lastEmailSent || "";
      if (date) {
        date = date.substr(0, 10);
        date = moment(date).format("DD.MM.YYYY") + ": ";
        return date + row.emailsSent;
      }
      return "";
    },
  },
  data() {
    let self = this;
    return {
      pagesize: 25,
      importingTimer: null,
      tableKey: 0,
      countrycodes,
      loading: false,
      subViews: ["main", "adder", "importer", "remover", "sender"],
      isMounted: false,
      letters: [],
      selectedLetter: null,
      deliveryMethods: ["print", "email"],
      selectedDeliveryMethod: null,
      updateTimer: null,
      view: "main",
      suppliers: [],
      widthListener: null,
      windowWidth: null,
      widthObj: "1800px",
      scrollListener: null,
      lettersSent: null,
      filters: {
        status: "",
        businessId: "",
        name: "",
        street1: "",
        street2: "",
        postalCode: "",
        postOffice: "",
        countryCode: "",
        email: "",
        language: "",
      },
      columnWidthOptions: {
        name: 400,
        street1: 1200,
        street2: 1200,
        postalCode: 1200,
        postOffice: 1200,
        countryCode: 1200,
        lastEmailSent: 780,
        lastLetterSent: 780,
        language: 600,
        email: 600,
        businessId: 400,
      },
      showColumn: {
        businessId: true,
        name: true,
        street1: true,
        street2: true,
        postalCode: true,
        postOffice: true,
        countryCode: true,
        email: true,
        language: true,
        lastEmailSent: true,
        lastLetterSent: true,
      },
      orderBy: {
        column: "name",
        isAscending: true,
      },

      selectedSuppliers: [],
      editableTextFields: [
        "name",
        "street1",
        "street2",
        "postOffice",
        "postalCode",
        "email",
      ],
      options: {
        orderBy: {
          column: "name",
        },
        skin: "supplier-table",
        texts: {
          count: this.$t("supplier.texts.count"),
          filter: this.$t("supplier.texts.filter"),
          filterPlaceholder: this.$t("supplier.texts.filterPlaceholder"),
          limit: self.$t("supplier.texts.limit"),
          page: this.$t("supplier.texts.page"),
          noResults: this.$t("supplier.texts.noResults"),
          filterBy: this.$t("supplier.texts.filterBy"),
          loading: this.$t("supplier.texts.loading"),
          defaultOption: this.$t("supplier.texts.defaultOptions"),
          columns: this.$t("supplier.texts.columns"),
        },

        sortIcon: {
          is: "fa-minus",
          base: "fa",
          up: "fa-arrow-up",
          down: "fa-arrow-down",
        },
        uniqueKey: "supplierId",
        pagination: {
          dropdown: false,
          chunk: 5,
        },
        perPage: 25,
        perPageValue: [10, 25, 50, 100],
        clientMultiSorting: true,
        compileTemplates: true,
        columnsClasses: {
          street1: "address-col",
          street2: "address-col",
          name: "name-col",
          businessId: "bid-col",
          postalCode: "",
          status: "status-col",
          language: "digit-col",
        },
        filterable: true,
        filterByColumn: true,
        sortable: [],
        // header filters
        headings: {
          select: function (h) {
            return h("input", {
              attrs: {
                type: "checkbox",
                id: "selectAllCheckbox",
                class: "form-control",
                style: "min-width:30px",
              },
              on: {
                click: (e) => {
                  this.selectAll(e.srcElement.checked);
                },
              },
              ref: "selectAllCheckbox",
            });
          },
          status: self.headerFilter("status", SelectFilter, [
            { name: "", value: "" },
            { name: "rowStatus.active", value: "active" },
            { name: "rowStatus.error", value: "error" },
            { name: "rowStatus.pending", value: "pending" },
            { name: "rowStatus.new", value: "new" },
          ]),
          name: self.headerFilter("name", TextFilter),
          street1: self.headerFilter("street1", TextFilter),
          street2: self.headerFilter("street2", TextFilter),
          postOffice: self.headerFilter("postOffice", TextFilter),
          postalCode: self.headerFilter("postalCode", TextFilter),
          countryCode: self.headerFilter("countryCode", SelectFilter, [
            { value: "", name: "" },
            ...countrycodes.map((cc) => ({ value: cc.value, name: cc.name })),
          ]),
          businessId: self.headerFilter("businessId", TextFilter),
          email: self.headerFilter("email", TextFilter),
          language: self.headerFilter("language", SelectFilter, [
            { name: "", value: "" },
            { name: "languages.finland", value: "fi_FI" },
            { name: "languages.sweden", value: "sv_SE" },
            { name: "languages.england", value: "en_US" },
            { name: "languages.germany", value: "de_DE" },
          ]),
          lastLetterSent: function (h) {
            return h("span", self.$t("supplier.lastLetterSent"));
          },
          lastEmailSent: function (h) {
            return h("span", self.$t("supplier.lastEmailSent"));
          },
        },
      },
    };
  },

  computed: {
    tableTexts: function () {
      return {
        count: this.$t("supplier.texts.count"),
        filter: this.$t("supplier.texts.filter"),
        filterPlaceholder: this.$t("supplier.texts.filterPlaceholder"),
        limit: self.$t("supplier.texts.limit"),
        page: this.$t("supplier.texts.page"),
        noResults: this.$t("supplier.texts.noResults"),
        filterBy: this.$t("supplier.texts.filterBy"),
        loading: this.$t("supplier.texts.loading"),
        defaultOption: this.$t("supplier.texts.defaultOptions"),
        columns: this.$t("supplier.texts.columns"),
      };
    },
    statusTexts: function () {
      return {
        active: this.$t("supplier.statusTexts.active"),
        new: this.$t("supplier.statusTexts.new"),
        pending: this.$t("supplier.statusTexts.pending"),
        error: this.$t("supplier.statusTexts.error"),
      };
    },
    statuses: function () {
      return this.suppliers.reduce(
        (acc, curr) => {
          acc[curr.status]++;
          return acc;
        },
        {
          new: 0,
          pending: 0,
          error: 0,
          active: 0,
          all: this.suppliers.length,
        }
      );
    },
    userData: function () {
      return this.$store.getters.userData;
    },
    userAddresses: function () {
      return this.$store.getters.userAddresses;
    },
    columns: function () {
      let w = this.windowWidth;
      let opts = this.columnWidthOptions;
      let cols = [];
      cols.push("select");
      cols.push("status");
      Object.keys(this.showColumn).forEach((key) => {
        if (this.showColumn[key] /* && w >= opts[key] */) {
          cols.push(key);
        }
      });
      return cols;
    },
  },
  methods: {
    top: function (e) {
      e = window.$(e);
      return e.offset().top;
    },

    bottom: function (e) {
      e = window.$(e);
      return e.offset().top + e.height();
    },
    updateList: async function () {
      try {
        let data = await this.$store.dispatch("listSuppliers");
        this.suppliers = data;
      } catch (error) {
        console.log(error);
      }
    },
    calculcateScrollVisiblity: function () {
      this.$nextTick(() => {
        this.displayScroll =
          this.$refs.supplierTable.$el.children[1].scrollWidth >
          this.$refs.supplierTable.$el.children[1].clientWidth;
        this.widthObj = {
          width:
            this.$refs.supplierTable.$el.children[1].scrollWidth + 30 + "px",
        };
      });
    },
    filterByStatus: function (status) {
      this.filters.status = status;
      this.$refs.supplierTable.setFilter({ status: status });
    },
    selectByStatus: function (status) {
      if (status == "all") {
        this.filters.status = "";
        this.$refs.supplierTable.setFilter({ status: "" });
        this.selectedSuppliers = this.suppliers;
      } else {
        this.filters.status = status;
        this.$refs.supplierTable.setFilter({ status: status });
        this.selectedSuppliers = this.suppliers.filter(
          (s) => s.status === status
        );
      }
    },

    addSupplier: function (data) {
      this.suppliers.push(...data);
    },
    updateSupplier: function (supplier) {
      if (supplier.supplierId === null) {
        let id = this.suppliers.find(
          (s) => s.businessId === supplier.businessId
        );
        if (id) {
          supplier.supplierId = id.supplierId;
        }
      }
      const idx = this.suppliers.findIndex(
        (s) => s.supplierId === supplier.supplierId
      );
      this.$set(this.suppliers, idx, supplier);
      this.selectedSuppliers = this.selectedSuppliers.filter(
        (s) => s.supplierId !== supplier.supplierId
      );
    },

    hideColumn: function (name) {
      if (this.$refs.supplierTable.allColumns.includes(name)) {
        this.$refs.supplierTable.toggleColumn(name);
      }
    },
    importSuppliers: async function () {
      try {
        let res = await this.$store.dispatch("importSuppliers");
        if (res.data === true) {
          this.importingTimer = setInterval(this.importerTimerFunction, 5000);
        }
      } catch (error) {
        console.log(error);
      }
    },
    checkImportStatus: async function () {
      try {
        let res = await this.$store.dispatch("checkImportingSuppliersStatus");
        if (res.data === true) {
          this.importingTimer = setInterval(this.importerTimerFunction, 5000);
        }
      } catch (error) {
        console.log(error);
      }
    },
    importerTimerFunction: async function () {
      try {
        let res = await this.$store.dispatch("checkImportingSuppliersStatus");
        if (res.data === true) {
          let data = await this.$store.dispatch("listSuppliers");
          this.suppliers = data;
          this.$notify({
            type: "success",
            text: this.$t("supplier.fetched"),
            duration: 3000,
          });
        } else {
          let data = await this.$store.dispatch("listSuppliers");
          this.$notify({
            type: "success",
            text: this.$t("supplier.fetched"),
            duration: 3000,
          });
          this.suppliers = data;
          clearInterval(this.importingTimer);
          this.importingTimer = null;
        }
      } catch (error) {
        console.log(error);
      }
    },
    remove: function () {
      this.$openModal({
        type: "confirm",
        text: this.$t("supplier.confirmRemove", {
          count: this.selectedSuppliers.length,
        }),
        title: this.$t("modal.remove"),
        callback: async () => {
          this.loading = true;
          try {
            let ids = this.selectedSuppliers.map((s) => s.supplierId);
            let res = await this.$store.dispatch("removeSuppliers", { ids });
            if (res.success) {
              this.suppliers = this.suppliers.filter(
                (s) => !ids.includes(s.supplierId)
              );
              this.selectedSuppliers = [];
              this.$notify({
                type: "success",
                text: this.$t("supplier.removeSuccessful", {
                  count: ids.length,
                }),
                duration: 3000,
              });
            }
          } catch (error) {
            console.log(error);
            this.$notify({
              type: "error",
              text: this.$t("supplier.operationFail"),
              duration: 3000,
            });
          } finally {
            this.$closeModal();
            this.loading = false;
          }
        },
      });
    },
    toggleView(view) {
      if (this.view === view) {
        this.view = null;
      } else {
        this.view = view;
      }
    },

    saveEdit: async function (id, field, value) {
      try {
        let idx = this.suppliers.findIndex((s) => id === s.supplierId);
        let s = this.suppliers[idx];
        if (s[field] !== value) {
          let update = { ...s, [field]: value };
          let res = await this.$store.dispatch("updateSupplier", update);
          if (res.success) {
            this.$set(this.suppliers, idx, update);
            const selectedIdx = this.selectedSuppliers.findIndex(
              (s) => s.supplierId === update.supplierId
            );
            if (selectedIdx > -1) {
              this.$set(this.selectedSuppliers, selectedIdx, update);
            }
            this.$notify({
              text: this.$t("supplier.updatedSupplier"),
              type: "success",
              duration: 3000,
            });
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    debounceEdit: function (id, field, value) {
      if (this.updateTimer) clearTimeout(this.updateTimer);
      this.updateTimer = setTimeout(() => {
        this.saveEdit(id, field, value);
      }, 1000);
    },
    selectAll(checked) {
      if (checked) {
        this.selectedSuppliers = this.$refs.supplierTable.filteredData;
      } else {
        this.selectedSuppliers = [];
      }
    },
    headerFilter: function (columnName, filter, values) {
      return function (h) {
        return h(filter, {
          props: {
            column: columnName,
            options: values,
            filterVal: this.filters[columnName],
            isAscending:
              this.orderBy.column === columnName
                ? this.orderBy.isAscending
                : null,
          },
          on: {
            updateFilter: (e) => {
              this.filters[columnName] = e;
              this.debounceSearch({ [columnName]: e });
            },
            updateOrderBy: (e) => {
              this.orderBy = e;
              this.setOrder(e);
            },
            updateValidation: (e) => {},
          },
        });
      };
    },
    setOrder: function ({ column, isAscending }) {
      this.$refs.supplierTable.setOrder(column, isAscending);
    },
    debounceSearch: function (query) {
      if (this.filterTimer) clearTimeout(this.filterTimer);
      this.filterTimer = setTimeout(() => {
        this.$refs.supplierTable.setFilter(query);
      }, 1000);
    },
    loadShowColumn: function () {
      const showColumn = localStorage.getItem("showColumn");
      if (showColumn) {
        this.showColumn = Object.assign(
          this.showColumn,
          JSON.parse(showColumn)
        );
      }
    },
  },
  watch: {
    showColumn: {
      handler: function (val) {
        localStorage.setItem("showColumn", JSON.stringify(val));
        this.calculcateScrollVisiblity();
      },
      deep: true,
    },
    pagesize: function (val) {
      this.$refs.supplierTable.setLimit(val);
    },
    windowWidth: function (val) {
      this.calculcateScrollVisiblity();
    },
  },
  components: {
    SuppliersMain,
    SuppliersRemover,
    SuppliersImporter,
    SuppliersSender,
    EditCellSelect,
    EditCell,
    EditCellEmail,
    SuppliersAdder,
    Modal,
  },
};
</script>

<style scoped>
.wrapper1 {
  display: block;
  overflow-x: scroll;
  position: fixed;
  width: 100%;
  bottom: 0px;
}
.div1 {
  height: 20px;
}

.status-list {
  margin: auto;
  display: flex;
  flex-direction: row;
  list-style: none;
  margin-bottom: 0;
  font-size: 20px;
}
.btn-text:hover {
  text-decoration: underline;
}
.btn-text {
  padding-top: 0;
  padding-bottom: 0;
  font-size: 20px;
}
.nav-tabs {
  border-bottom: 2px solid #468e9e;
}
.nav-item {
  width: 20%;
}
.nav-link {
  font-size: 1.5rem;
  cursor: pointer;
  border-bottom: 2px solid #468e9e;
}
.nav-link.active {
  color: #fff;
  background-color: #367b8b;
  border: 1px solid;
  border-bottom: none;
  border-color: #468e9e #468e9e #468e9e;

  border-bottom: solid 2px rgb(255, 115, 0);
}

.nav-link:not(.active):hover {
  color: #fff;
  background-color: #367b8b;
  border: none;
  border: 1px solid transparent;
}
@media screen and (max-width: 768px) {
  .nav-link {
    height: 100%;
  }
}
@media screen and (max-width: 425px) {
  .nav-link {
    font-size: 0.7rem;
  }
}
@media screen and (max-width: 320px) {
  .nav-link {
    font-size: 0.7rem;
  }
}
</style>
<style>
table {
  color: black;
  width: 100%;
}
.bid-col {
  min-width: 100px;
  text-align: left;
  padding-left: 5px !important;
}
.status-col {
  max-width: 100px;
}
.address-col {
  min-width: 100px;
}

.invoice-table {
  border: 1px solid rgba(0, 0, 0, 0.2);
}

.invoice-table thead {
  color: #fff;
}

.circle {
  margin: auto;
  border-radius: 50%;
  width: 1.5em;
  height: 1.5em;

  cursor: pointer;
}

.circle_green {
  background-color: green;
}

.circle_yellow {
  background-color: rgb(255, 196, 0);
}

.circle_red {
  background-color: red;
}

.circle_gray {
  background-color: gray;
}

.invoice-table td,
th {
  border-left: 1px solid rgba(0, 0, 0, 0.2);
  padding: 8px;
}
.invoice-table td:first-child,
th:first-child {
  border-left: none;
}
.table > thead > tr > th {
  padding: 10px 0px 0px 0px;
}
.VueTables__child-row > td {
  padding: 0 !important;
}
.VuePagination .page-item:last-child,
.page-item:first-child {
  display: none;
}
.VueTables__child-row-toggler {
  width: 16px;
  height: 16px;
  line-height: 16px;
  display: block;
  margin: auto;
  text-align: center;
}

.VueTables__child-row-toggler--closed::before {
  font-family: FontAwesome;
  content: "\f067";
  /* content: "+"; */
  cursor: pointer;
}

.VueTables__child-row-toggler--open::before {
  font-family: FontAwesome;
  content: "\f068";
  cursor: pointer;
}
.invoice-table th:first-child::after {
}

/* .header:focus-within > label {
  top: -10px;
} */
.VueTables__sortable {
  position: relative;
}

/* .table-responsive {
  overflow: visible !important;
}
 */
.page-navigator > button {
  margin: 0;
  padding: 15px;
  border-radius: 0px;
}

/* Enter and leave animations can use different */
/* durations and timing functions.              */
.slide-fade-enter-active {
  overflow: hidden;

  transition: height 0.3s linear;
}
.slide-fade-leave-active {
  overflow: hidden;

  transition: height 0.3s linear;
}
.slide-fade-enter-to,
.slide-fade-leave {
  overflow: hidden;
  height: 450px;
}
.slide-fade-enter, .slide-fade-leave-to
/* .slide-fade-leave-active below version 2.1.8 */ {
  height: 0px;
}

.dropdown {
  margin-top: 1.5em;
  float: left;
}

.VuePagination .page-link {
  color: #96cad4;
  background-color: rgba(0, 0, 0, 0.25);
  border: none;
}
.VuePagination .page-link:focus {
  outline: #96cad4 1px solid;
  box-shadow: none;
}

.VuePagination .page-link:hover {
  color: #96cad4;
  background-color: rgba(0, 0, 0, 0.05);
}

.VuePagination .page-item.active .page-link {
  background-color: rgba(0, 0, 0, 0.05);
  border: none;
}

.VuePagination .page-item.disabled .page-link {
  background-color: rgba(0, 0, 0, 0.05);
}
.VuePagination__count {
  display: none;
}

.opener-icon {
  width: 35px;
  cursor: pointer;
  font-size: 35px !important;
}

.loading-dots:after {
  content: " .";
  font-size: 20px;
  animation: dots 1s steps(5, end) infinite;
}

.VueTables__filters-row {
  display: none;
}
.VueTables__limit {
  float: left;
}
.VueTables__limit-field {
  display: none;
}
.supplier-table-wrapper {
  margin-top: 1em;
}
.supplier-table {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);
}
.supplier-table td {
  border: 1px solid rgba(0, 0, 0, 0.2);
  padding: 0;
}
table {
  width: 100%;
}

td {
  position: relative;
}

.suppliers-actions select {
  border-radius: 0 !important;
}

.suppliers-actions button {
  border-radius: 0 !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
.dropdown-menu-item > label {
  float: left;
  clear: both;
  cursor: pointer;
}
.dropdown-menu-item > label:hover {
  color: rgba(0, 0, 0, 0.5);
}

@keyframes dots {
  0%,
  20% {
    color: rgba(0, 0, 0, 0);
    text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  40% {
    color: white;
    text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  60% {
    text-shadow: 0.25em 0 0 white, 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  80%,
  100% {
    text-shadow: 0.25em 0 0 white, 0.5em 0 0 white;
  }
}

@media screen and (max-width: 1025px) {
  .invoice-table th:first-child::after {
    font-size: 14px;
  }
}
@media screen and (max-width: 425px) {
  .status-list .btn-text {
    font-size: 14px;
  }
  .status-list {
    padding-left: 0;
  }
  .invoice-table td,
  th {
    padding: 2px;
    font-size: 14px;
  }
}
</style>
